import { useAuth, useClerk } from "@clerk/clerk-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import closeIcon from "../assets/cross.svg";

// Idle-logout policy (admin console). 15 min is the typical default for
// admin tools handling sensitive data; 30 sec on the confirmation prompt
// is enough to grab attention without being so long that an unattended
// session sits open. Tuneable here if compliance ever changes.
const IDLE_TIMEOUT_MS = 15 * 60 * 1000;
const PROMPT_COUNTDOWN_SEC = 30;
// Throttle so a busy mouse doesn't reset the inactivity timer thousands of
// times per second.
const ACTIVITY_THROTTLE_MS = 1000;

// Passive listeners only — we never call preventDefault, so the browser can
// keep the scrolling/touch fast-path. Capture phase ensures we see the
// event even when child elements stopPropagation.
const ACTIVITY_EVENTS: Array<keyof WindowEventMap> = [
  "mousedown",
  "keydown",
  "touchstart",
  "wheel",
  "scroll",
];

/**
 * Renders nothing while the user is active. After IDLE_TIMEOUT_MS without
 * input, shows an "Are you still there?" modal with a PROMPT_COUNTDOWN_SEC
 * countdown; on expiry calls Clerk signOut and redirects to /signin. While
 * the modal is open, page activity is intentionally ignored — the user must
 * explicitly click "Stay signed in" to keep the session.
 *
 * Per-tab (no cross-tab localStorage sync). Skips on /signin so the prompt
 * never appears on the auth page itself.
 */
export default function IdleLogoutWatcher() {
  const { isSignedIn, isLoaded } = useAuth();
  const { signOut } = useClerk();
  const location = useLocation();

  const [promptOpen, setPromptOpen] = useState(false);
  const [remainingSec, setRemainingSec] = useState(PROMPT_COUNTDOWN_SEC);
  const [signingOut, setSigningOut] = useState(false);

  const idleTimerRef = useRef<number | null>(null);
  const countdownRef = useRef<number | null>(null);
  const lastActivityRef = useRef<number>(Date.now());
  // Mirror promptOpen in a ref so the activity handler reads the latest
  // value without forcing a re-bind of the listeners every render.
  const promptOpenRef = useRef(false);
  promptOpenRef.current = promptOpen;

  const isOnSignInRoute = location.pathname.startsWith("/signin");
  const active = isLoaded && isSignedIn && !isOnSignInRoute;

  const clearIdleTimer = useCallback(() => {
    if (idleTimerRef.current !== null) {
      window.clearTimeout(idleTimerRef.current);
      idleTimerRef.current = null;
    }
  }, []);

  const clearCountdown = useCallback(() => {
    if (countdownRef.current !== null) {
      window.clearInterval(countdownRef.current);
      countdownRef.current = null;
    }
  }, []);

  const handleSignOut = useCallback(async () => {
    if (signingOut) return;
    setSigningOut(true);
    clearIdleTimer();
    clearCountdown();
    try {
      await signOut({ redirectUrl: "/signin" });
    } catch (err) {
      console.error("Idle logout failed:", err);
      // Fall back to a hard navigation so the user still ends up at sign-in
      // even if Clerk's signOut threw.
      window.location.assign("/signin");
    }
  }, [signOut, signingOut, clearIdleTimer, clearCountdown]);

  const openPrompt = useCallback(() => {
    setRemainingSec(PROMPT_COUNTDOWN_SEC);
    setPromptOpen(true);
  }, []);

  const scheduleIdleCheck = useCallback(() => {
    clearIdleTimer();
    idleTimerRef.current = window.setTimeout(() => {
      // Defensive: re-check elapsed time. Background tabs may have throttled
      // the timer so it fires before IDLE_TIMEOUT_MS has truly elapsed.
      const elapsed = Date.now() - lastActivityRef.current;
      if (elapsed >= IDLE_TIMEOUT_MS) {
        openPrompt();
      } else {
        idleTimerRef.current = window.setTimeout(() => openPrompt(), IDLE_TIMEOUT_MS - elapsed);
      }
    }, IDLE_TIMEOUT_MS);
  }, [clearIdleTimer, openPrompt]);

  const handleStaySignedIn = useCallback(() => {
    clearCountdown();
    setPromptOpen(false);
    setRemainingSec(PROMPT_COUNTDOWN_SEC);
    lastActivityRef.current = Date.now();
    scheduleIdleCheck();
  }, [clearCountdown, scheduleIdleCheck]);

  // ── Activity listeners ────────────────────────────────────────────────
  useEffect(() => {
    if (!active) return;

    lastActivityRef.current = Date.now();
    scheduleIdleCheck();

    const onActivity = () => {
      // Ignore page activity once the prompt is open: the user must click
      // "Stay signed in" to keep the session — that's the whole point of
      // the confirmation step.
      if (promptOpenRef.current) return;
      const now = Date.now();
      if (now - lastActivityRef.current < ACTIVITY_THROTTLE_MS) return;
      lastActivityRef.current = now;
      scheduleIdleCheck();
    };

    for (const evt of ACTIVITY_EVENTS) {
      window.addEventListener(evt, onActivity, { passive: true, capture: true });
    }

    return () => {
      for (const evt of ACTIVITY_EVENTS) {
        window.removeEventListener(evt, onActivity, { capture: true } as EventListenerOptions);
      }
      clearIdleTimer();
    };
  }, [active, scheduleIdleCheck, clearIdleTimer]);

  // ── Countdown while prompt is open ────────────────────────────────────
  useEffect(() => {
    if (!promptOpen) return;
    clearCountdown();
    countdownRef.current = window.setInterval(() => {
      setRemainingSec((sec) => {
        if (sec <= 1) {
          clearCountdown();
          // Defer signOut call out of the setState callback.
          window.setTimeout(() => handleSignOut(), 0);
          return 0;
        }
        return sec - 1;
      });
    }, 1000);

    return () => clearCountdown();
  }, [promptOpen, clearCountdown, handleSignOut]);

  // ── Reset everything when the user signs out / leaves protected area ──
  useEffect(() => {
    if (active) return;
    clearIdleTimer();
    clearCountdown();
    setPromptOpen(false);
    setRemainingSec(PROMPT_COUNTDOWN_SEC);
  }, [active, clearIdleTimer, clearCountdown]);

  if (!promptOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/40 z-[100] flex justify-center items-center font-['Manrope']">
      <div className="w-[460px] max-w-[92vw] bg-white rounded-[20px] overflow-hidden shadow-2xl">
        <div className="p-6 bg-gradient-to-br from-white to-blue-100 border-b border-blue-100 flex justify-between items-center">
          <h2 className="text-[18px] font-bold text-gray-900">Are you still there?</h2>
          <button
            type="button"
            onClick={handleStaySignedIn}
            disabled={signingOut}
            className="p-1 disabled:opacity-50"
            aria-label="Stay signed in"
          >
            <img src={closeIcon} alt="" className="w-5 h-5" />
          </button>
        </div>

        <div className="px-6 pt-6 pb-6 flex flex-col gap-6">
          <div className="text-[16px] leading-normal">
            <span className="text-gray-500 font-medium">
              For your security, you'll be signed out in{" "}
            </span>
            <span className="text-gray-900 font-bold">{remainingSec} seconds</span>
            <span className="text-gray-500 font-medium">
              {" "}due to inactivity. Click below to stay signed in.
            </span>
          </div>

          <div className="flex justify-end gap-2">
            <button
              type="button"
              onClick={handleSignOut}
              disabled={signingOut}
              className="h-11 px-4 py-1.5 text-[16px] font-medium text-gray-900 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
            >
              Sign out now
            </button>
            <button
              type="button"
              onClick={handleStaySignedIn}
              disabled={signingOut}
              className="h-11 px-4 py-1.5 text-[16px] font-medium text-white bg-primary rounded-lg hover:bg-primary-600 transition-colors disabled:opacity-50"
            >
              Stay signed in
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
